/**
 * Requested byes — "I can't play round 3, give me a half point."
 *
 * WHY THIS IS NOT THE PRESENT/ABSENT FLAG. A player's `status` is one boolean for the
 * whole tournament: turn it off and they are left out of every round until somebody
 * remembers to turn it back on, and the round they miss is worth nothing. That is the
 * right model for a club night, where who turns up is discovered at pairing time, and
 * the wrong one for a weekend Swiss, where three people tell the arbiter on Thursday
 * which session they will miss and expect the half point the invitation promised. This
 * file is that second thing: a request attached to a ROUND, made in advance, that the
 * arbiter can see and change before the round is paired.
 *
 * WHAT A REQUEST DOES.
 *   'half'  the player is not paired, and gets a pairing against BYE worth half a point.
 *   'zero'  the player is not paired, and gets a pairing against BYE worth nothing.
 * Both produce a visible row on the pairing list, which is the point of planning a bye
 * in advance — the player looks at the list and sees their own name against "BYE"
 * rather than not finding it at all. (Konrad's absences deliberately work the other way,
 * see js/core/konrad-engine.js: there an absence is discovered at pairing time and a
 * zero-point one is left out entirely.)
 *
 * A full-point bye is NOT offered. FIDE's Dutch system reserves a full point for the
 * pairing-allocated bye — the one the engine hands the odd player out — and awarding one
 * on request hands a player a win they did not play for, in a tournament that will be
 * rated. The arbiter who genuinely needs one can still enter it by hand on the pairing
 * row afterwards; nothing here stops them, and the TRF export already writes a
 * requested full-point bye as 'F' rather than 'U'.
 *
 * WHAT IS STORED. `player.byeRequests` — a plain object keyed by ROUND NUMBER (1-based,
 * as a string, which is what a JSON round-trip through Firestore gives back anyway):
 *
 *     { "3": "half", "7": "zero" }
 *
 * Rounds with no entry are ordinary rounds. An empty or absent map is the common case and
 * costs a player object nothing.
 *
 * A classic <script> (window.ByeRequests), because js/services/pairing-trf.js is one and
 * has to ask "is this player sitting this round out?" while building the file. Pure: no
 * DOM, no state, no Firestore. Tests load it with tests/helpers/load-classic-script.js.
 */
(function (root) {
    'use strict';

    // The values a request may take, in the order the UI cycles through them.
    var VALUES = ['half', 'zero'];

    // What each one is recorded as on the pairing row. The shape — the player on the
    // white side against 'BYE' — is the app's one way of writing a bye, so standings,
    // tie-breaks and the TRF export read these without a special case:
    // SwissEngine.calculateStats credits '0.5-0.5' with a draw and anything else with
    // nothing, and TRFExport.buildCells turns them into the codes 'H' and 'Z'.
    var RESULT = { half: '0.5-0.5', zero: '0-1' };

    /**
     * Which tournament formats honour a requested bye.
     *
     * Swiss, Monrad and the Arena pair whoever is in the field, so taking a player out of
     * it for one round is a request the arbiter can grant. A round robin cannot: its schedule is
     * fixed over the whole field before round 1, and a player who misses a round there
     * forfeits a game already assigned to them, entered as a result. Konrad already has
     * its own account of who is here and what an absence is worth
     * (js/core/konrad-engine.js), and two systems for the same thing would pay a player
     * twice for one evening away.
     *
     * Lives here rather than in either of the two UI modules that ask, because they must
     * agree: js/ui/byes.js decides whether to OFFER the grid and js/ui/pairings.js decides
     * whether to ACT on what it holds, and a format where one said yes and the other no
     * would take requests it then silently ignored.
     */
    function honoursFormat(format) {
        return format === 'swiss' || format === 'monrad' || format === 'arena';
    }

    function roundKey(roundNo) {
        var n = parseInt(roundNo, 10);
        return Number.isFinite(n) && n > 0 ? String(n) : null;
    }

    /**
     * Read anything shaped roughly like a request map into a clean one: round numbers as
     * string keys, values limited to the ones above, everything else dropped.
     *
     * Hand-edited data, an older file and a future value all land here, and a map that
     * silently kept one of them would leave a player unpaired for a reason the UI cannot
     * show and the arbiter cannot clear.
     */
    function normalize(raw) {
        var out = {};
        if (!raw || typeof raw !== 'object') return out;
        Object.keys(raw).forEach(function (k) {
            var key = roundKey(k);
            if (key && VALUES.indexOf(raw[k]) >= 0) out[key] = raw[k];
        });
        return out;
    }

    /** The request a player has for one round, or null. */
    function requestFor(player, roundNo) {
        var key = roundKey(roundNo);
        if (!key || !player) return null;
        var map = normalize(player.byeRequests);
        return map[key] || null;
    }

    /**
     * A copy of `map` with one round set (value 'half'/'zero') or cleared (null).
     * Returns a new object rather than mutating, so a caller can diff before saving.
     */
    function withRequest(map, roundNo, value) {
        var out = normalize(map);
        var key = roundKey(roundNo);
        if (!key) return out;
        if (VALUES.indexOf(value) >= 0) out[key] = value;
        else delete out[key];
        return out;
    }

    /** The next value in the cycle none -> half -> zero -> none, for a click-to-cycle UI. */
    function nextValue(value) {
        var i = VALUES.indexOf(value);
        if (i < 0) return VALUES[0];
        return i + 1 < VALUES.length ? VALUES[i + 1] : null;
    }

    /**
     * Should this player be left out of the pairing for this round?
     *
     * Only asks about the request. Being marked absent is a separate, older reason to be
     * left out and every caller already checks it.
     */
    function isExcluded(player, roundNo) {
        return requestFor(player, roundNo) !== null;
    }

    /** The ids of everyone sitting this round out by request. */
    function excludedIds(players, roundNo) {
        return (players || []).filter(function (p) { return isExcluded(p, roundNo); })
            .map(function (p) { return p.id; });
    }

    /**
     * The bye pairings to append to a round after the engine has paired everyone else.
     *
     * A player who is ALSO marked absent gets nothing: they are already outside the
     * tournament for this round, and handing them the half point they asked for while
     * they are marked away would pay them twice over for one absence. The request stays
     * on the player, so marking them present again brings it back.
     *
     * @param {Array}  players  the whole field
     * @param {number} roundNo  the round being paired (1-based)
     * @returns {Array} pairings in the app's shape, ready to push onto the round
     */
    function pairingsFor(players, roundNo) {
        return (players || [])
            .filter(function (p) { return p && p.status && isExcluded(p, roundNo); })
            .map(function (p) {
                return {
                    white: p.id,
                    black: 'BYE',
                    result: RESULT[requestFor(p, roundNo)],
                    // Tells this apart from the odd-player bye the engine allocates. The
                    // TRF export needs it (a requested bye is never 'U'), and Monrad's
                    // §12A needs it: sitting out by choice must not use up the allocated
                    // bye a player would otherwise be owed.
                    requestedBye: true
                };
            });
    }

    /**
     * Every request a player has from `fromRound` onwards, oldest first — what a badge on
     * the player list shows. Rounds already played are left out because a request for them
     * is history, not a plan.
     *
     * @returns {Array<{round: number, value: string}>}
     */
    function upcoming(player, fromRound) {
        var from = parseInt(fromRound, 10) || 1;
        var map = normalize(player && player.byeRequests);
        return Object.keys(map)
            .map(function (k) { return { round: parseInt(k, 10), value: map[k] }; })
            .filter(function (e) { return e.round >= from; })
            .sort(function (a, b) { return a.round - b.round; });
    }

    /** Does anyone in the field have a request at all? Gates the UI that shows them. */
    function anyRequests(players) {
        return (players || []).some(function (p) {
            return Object.keys(normalize(p && p.byeRequests)).length > 0;
        });
    }

    root.ByeRequests = {
        VALUES: VALUES,
        RESULT: RESULT,
        honoursFormat: honoursFormat,
        normalize: normalize,
        requestFor: requestFor,
        withRequest: withRequest,
        nextValue: nextValue,
        isExcluded: isExcluded,
        excludedIds: excludedIds,
        pairingsFor: pairingsFor,
        upcoming: upcoming,
        anyRequests: anyRequests
    };
})(typeof window !== 'undefined' ? window : globalThis);
